/**
 * Extracts and sanitizes the filename from a `Content-Disposition` response header.
 *
 * This is more robust than naive `split(';')`/index based parsing because:
 * - it does not assume a fixed parameter order or position (e.g. `attachment; filename=...`
 *   vs. `attachment; filename*=UTF-8''...; filename=...`),
 * - it supports the RFC 5987 extended `filename*=` parameter (percent-encoded, with charset),
 *   preferring it over the plain `filename=` parameter as required by RFC 6266,
 * - it sanitizes the extracted value, stripping path separators and control characters so a
 *   (possibly server- or proxy-modified) header value can never be used to smuggle a path
 *   traversal or otherwise unexpected characters into a client-side download filename.
 *
 * @param contentDisposition the raw `Content-Disposition` header value (may be null/undefined)
 * @param fallback filename to use if none could be extracted
 */
export function extractFilenameFromContentDisposition(
  contentDisposition: string | null | undefined,
  fallback = 'download'
): string {
  if (!contentDisposition) {
    return fallback;
  }

  const extendedMatch = /filename\*\s*=\s*(?:[\w-]+)?''([^;\n]+)/i.exec(contentDisposition);
  if (extendedMatch?.[1]) {
    try {
      const sanitized = sanitizeFilename(decodeURIComponent(extendedMatch[1].trim()));
      if (sanitized) {
        return sanitized;
      }
    } catch {
      // malformed percent-encoding -> fall through to the plain filename parameter below
    }
  }

  const plainMatch = /(?<!\*)filename\s*=\s*(?:"([^"]*)"|'([^']*)'|([^;\n]*))/i.exec(contentDisposition);
  if (plainMatch) {
    const raw = (plainMatch[1] ?? plainMatch[2] ?? plainMatch[3] ?? '').trim();
    const sanitized = sanitizeFilename(raw);
    if (sanitized) {
      return sanitized;
    }
  }

  return fallback;
}

function sanitizeFilename(filename: string): string {
  const withoutPathSeparators = filename.replace(/[\\/]/g, '');
  let result = '';
  for (const char of withoutPathSeparators) {
    const code = char.codePointAt(0);
    // strip control characters (0x00-0x1f, 0x7f) without triggering the no-control-regex lint rule
    if (code !== undefined && code >= 0x20 && code !== 0x7f) {
      result += char;
    }
  }
  return result.trim();
}

